import { useCallback, useEffect } from "react";
import { Contractor, CreateContractor, UpdateContractor } from "../types/models/Contractor.d";
import { UpdateEntityContractor } from "../types/models/Contractor";
import { useGetContractorListQuery, useInsertContractorMutation, useUpdateContractorMutation, useDeleteContractorMutation, } from "../apis/Contractor.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useContractor(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertContractorMutation, { 
        isLoading: insertContractorLoading, 
        isSuccess: insertContractorIsSuccess,
        isError: insertContractorIsError,
        error: insertContractorError,
    }] = useInsertContractorMutation()
    const [updateContractorMutation, { 
        isLoading: updateContractorLoading,
        isSuccess: updateContractorIsSuccess,
        isError: updateContractorIsError,
        error: updateContractorError,
    }] = useUpdateContractorMutation()
    const [deleteContractorMutation, { 
        isLoading: deleteContractorLoading,
        isSuccess: deleteContractorIsSuccess,
        isError: deleteContractorIsError,
        error: deleteContractorError,
    }] = useDeleteContractorMutation()

    const loading =
        insertContractorLoading ||
        updateContractorLoading ||
        deleteContractorLoading ||
        false

    const insertContractor = useCallback(async (contractor: CreateContractor, redirectPath: string|undefined) => {
        try {
            const data = await insertContractorMutation(contractor).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertContractorMutation, navigate])
    
    useEffect(() => {
        if (insertContractorIsSuccess) {
            openSuccessAlert('Contractor created')
        } else if (insertContractorIsError) {
            console.log("add error", insertContractorError)
            openFailAlert(t`Contractor add error: ` + insertContractorError?.data?.message)
        }
    }, [
        insertContractorIsSuccess,
        insertContractorIsError,
        insertContractorError
    ]);

    const updateContractor = useCallback(async (contractor: UpdateEntityContractor, redirectPath: string|undefined) => {
        try {
            const data = await updateContractorMutation(contractor).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateContractorMutation, navigate])
    
    useEffect(() => {
        if (updateContractorIsSuccess) {
            openSuccessAlert(t`Contractor updated`)
        } else if (updateContractorIsError) {
            console.log("update error", updateContractorError)
            openFailAlert(t`Contractor update error: ` + updateContractorError?.data?.message)
        }
    }, [
        updateContractorIsSuccess,
        updateContractorIsError,
        updateContractorError
    ]);

    const deleteContractor = useCallback((contractor: Contractor, redirectPath: string|undefined) => {
        openDialog(async () => {
            contractor = {
                ...contractor,
            }
            try {
                const { data } = await deleteContractorMutation(contractor);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteContractorMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteContractorIsSuccess) {
            openSuccessAlert(t`Contractor deleted`)
        } else if (deleteContractorIsError) {
            console.log("delete error", deleteContractorError)
            openFailAlert(t`Contractor delete error: ` + deleteContractorError?.data?.message)
        }
    }, [
        deleteContractorIsSuccess,
        deleteContractorIsError,
        deleteContractorError
    ]);

    return {
        loading,
        insertContractor,
        updateContractor,
        deleteContractor
    }
}
